import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';

/** @type {WeakMap<HTMLElement, HTMLElement>} */
const centeredPanelHosts = new WeakMap();

/**
 * @typedef {object} Variant
 * @property {number} id
 * @property {string[]} options
 * @property {boolean} available
 */

/**
 * Cart page line item option and quantity dropdowns.
 *
 * @typedef {object} Refs
 * @property {HTMLSelectElement[]} [optionSelects]
 * @property {HTMLSelectElement} [quantitySelect]
 * @property {HTMLScriptElement} [variantsData]
 * @property {HTMLElement} [sizePicker]
 * @property {HTMLButtonElement} [sizeTrigger]
 * @property {HTMLElement} [sizePanel]
 * @property {HTMLButtonElement} [sizeClose]
 * @property {HTMLButtonElement} [sizeDone]
 * @property {HTMLElement} [sizeHeadingValue]
 * @property {HTMLElement} [sizeTriggerText]
 * @property {HTMLButtonElement[]} [sizeOptions]
 * @property {HTMLElement} [colorPicker]
 * @property {HTMLButtonElement} [colorTrigger]
 * @property {HTMLElement} [colorPanel]
 * @property {HTMLButtonElement} [colorClose]
 * @property {HTMLButtonElement} [colorDone]
 * @property {HTMLElement} [colorHeadingValue]
 * @property {HTMLElement} [colorTriggerText]
 * @property {HTMLButtonElement[]} [colorOptions]
 * @property {HTMLElement} [colorAvailabilityError]
 *
 * @extends {Component<Refs>}
 */
class CartLineOptions extends Component {
  /** @type {boolean} */
  #isUpdating = false;

  /** @type {Map<string, { variants: Array<{ id: number, available: boolean, options?: string[], option1?: string | null, option2?: string | null, option3?: string | null }>, featured_image?: string | null, images?: string[] }>} */
  #familyProductCache = new Map();

  /** @type {string | null} */
  #committedSizeValue = null;

  /** @type {string | null} */
  #pendingSizeValue = null;

  /** @type {((event: MouseEvent) => void) | null} */
  #sizeOutsideClickHandler = null;

  /** @type {((event: KeyboardEvent) => void) | null} */
  #sizeKeydownHandler = null;

  /** @type {string | null} */
  #committedColorValue = null;

  /** @type {string | null} */
  #pendingColorValue = null;

  /** @type {string | null} */
  #committedFamilyHandle = null;

  /** @type {string | null} */
  #pendingFamilyHandle = null;

  /** @type {((event: MouseEvent) => void) | null} */
  #familySwatchClickHandler = null;

  /** @type {((event: MouseEvent) => void) | null} */
  #colorOutsideClickHandler = null;

  /** @type {((event: KeyboardEvent) => void) | null} */
  #colorKeydownHandler = null;

  /**
   * Portaled centered panels leave `this.refs` (Component only tracks descendants).
   * Keep direct references so Done/Close still work after size morph + reopen.
   * @type {HTMLElement | null}
   */
  #mountedSizePanel = null;

  /** @type {HTMLElement | null} */
  #mountedColorPanel = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#onChange);
    this.#setupSizePicker();
    this.#setupColorPicker();
    sessionStorage.removeItem('cartColorPickerOpenVariant');
  }

  /**
   * Re-bind pickers after section morph reuses this element for a (possibly different) line.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#teardownSizePickerListeners();
    this.#teardownColorPickerListeners();
    this.#mountedSizePanel = null;
    this.#mountedColorPanel = null;
    this.#setupSizePicker();
    this.#setupColorPicker();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);
    this.#teardownSizePickerListeners();
    this.#teardownColorPickerListeners();
    this.#restoreCenteredPanel(this.#getSizePanelElement());
    this.#restoreCenteredPanel(this.#getColorPanelElement());
  }

  #usesFamilySwatches() {
    return this.dataset.hasFamilySwatches !== undefined;
  }

  #usesCenteredModals() {
    return this.classList.contains('cart-line-options--centered-modals');
  }

  /** @returns {HTMLElement | null} */
  #getSizePanelElement() {
    if (this.#mountedSizePanel instanceof HTMLElement) return this.#mountedSizePanel;
    if (this.refs.sizePanel instanceof HTMLElement) return this.refs.sizePanel;

    const fromPicker = this.refs.sizePicker?.querySelector('.cart-size-picker__panel');
    return fromPicker instanceof HTMLElement ? fromPicker : null;
  }

  /** @returns {HTMLElement | null} */
  #getColorPanelElement() {
    if (this.#mountedColorPanel instanceof HTMLElement) return this.#mountedColorPanel;
    if (this.refs.colorPanel instanceof HTMLElement) return this.refs.colorPanel;

    const fromPicker = this.refs.colorPicker?.querySelector('.cart-color-picker__panel');
    return fromPicker instanceof HTMLElement ? fromPicker : null;
  }

  /** @param {HTMLElement} panel */
  #isPanelOpen(panel) {
    if (panel instanceof HTMLDialogElement) return panel.open;
    return panel.hidden === false;
  }

  /**
   * @param {HTMLElement} panel
   * @param {HTMLElement | undefined} host
   */
  #openPanel(panel, host) {
    if (this.#usesCenteredModals()) {
      this.#mountCenteredPanel(panel, host);
      if (panel instanceof HTMLDialogElement && !panel.open) {
        panel.showModal();
      }
      return;
    }

    panel.hidden = false;
  }

  /**
   * @param {HTMLElement} panel
   */
  #closePanel(panel) {
    if (panel instanceof HTMLDialogElement && panel.open) {
      panel.close();
    } else {
      panel.hidden = true;
    }

    this.#restoreCenteredPanel(panel);
  }

  /**
   * @param {HTMLElement | undefined} panel
   * @param {HTMLElement | undefined} host
   */
  #mountCenteredPanel(panel, host) {
    if (!(panel instanceof HTMLElement) || !(host instanceof HTMLElement) || !this.#usesCenteredModals()) {
      return;
    }

    if (panel.classList.contains('cart-size-picker__panel')) {
      this.#mountedSizePanel = panel;
    }
    if (panel.classList.contains('cart-color-picker__panel')) {
      this.#mountedColorPanel = panel;
    }

    if (panel.parentElement === document.body) return;

    centeredPanelHosts.set(panel, host);
    document.body.appendChild(panel);
  }

  /** @param {HTMLElement | undefined | null} panel */
  #restoreCenteredPanel(panel) {
    if (!(panel instanceof HTMLElement) || !this.#usesCenteredModals()) return;

    const host = centeredPanelHosts.get(panel);
    if (host instanceof HTMLElement && panel.parentElement === document.body) {
      host.appendChild(panel);
      centeredPanelHosts.delete(panel);
    }

    if (this.#mountedSizePanel === panel) this.#mountedSizePanel = null;
    if (this.#mountedColorPanel === panel) this.#mountedColorPanel = null;
  }

  /**
   * @param {HTMLElement} picker
   */
  #hidePickerPanels(picker) {
    picker.classList.remove('is-open');

    const sizePanel =
      (picker.querySelector('.cart-size-picker__panel') instanceof HTMLElement
        ? picker.querySelector('.cart-size-picker__panel')
        : null) || (picker.classList.contains('cart-size-picker') ? this.#getSizePanelElement() : null);
    const sizeTrigger = picker.querySelector('.cart-size-picker__trigger');
    if (sizePanel instanceof HTMLElement) {
      this.#closePanel(sizePanel);
    }
    if (sizeTrigger instanceof HTMLButtonElement) sizeTrigger.setAttribute('aria-expanded', 'false');

    const colorPanelFromPicker = picker.querySelector('.cart-color-picker__panel');
    const colorPanel =
      (colorPanelFromPicker instanceof HTMLElement ? colorPanelFromPicker : null) ||
      (picker.classList.contains('cart-color-picker') ? this.#getColorPanelElement() : null);
    const colorTrigger = picker.querySelector('.cart-color-picker__trigger');
    if (colorPanel instanceof HTMLElement) {
      this.#closePanel(colorPanel);
    }
    if (colorTrigger instanceof HTMLButtonElement) colorTrigger.setAttribute('aria-expanded', 'false');
  }

  /** @param {Event} event */
  #onChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    if (this.#isUpdating || this.dataset.optionsDisabled !== undefined) return;

    if (event.target === this.refs.quantitySelect) {
      this.#updateQuantity(event.target);
      return;
    }

    if (
      event.target.classList.contains('cart-line-options__select--size') ||
      event.target.classList.contains('cart-line-options__select--color')
    ) {
      return;
    }

    this.#updateVariant();
  };

  #setupSizePicker() {
    const { sizePicker, sizeTrigger, sizePanel, sizeClose, sizeDone } = this.refs;
    if (!(sizePicker instanceof HTMLElement) || !(sizeTrigger instanceof HTMLButtonElement)) return;

    sizeTrigger.addEventListener('click', this.#onSizeTriggerClick);
    sizeClose?.addEventListener('click', this.#onSizeCloseClick);
    sizeDone?.addEventListener('click', this.#onSizeDoneClick);
    if (sizePanel instanceof HTMLDialogElement) {
      sizePanel.addEventListener('cancel', this.#onSizeDialogCancel);
    }

    const { sizeOptions } = this.refs;
    if (sizeOptions) {
      const options = Array.isArray(sizeOptions) ? sizeOptions : [sizeOptions];
      options.forEach((button) => {
        if (button instanceof HTMLButtonElement) {
          button.addEventListener('click', this.#onSizeOptionClick);
        }
      });
    }

    this.#committedSizeValue = this.#getSizeSelect()?.value ?? null;
  }

  #teardownSizePickerListeners() {
    const { sizeTrigger, sizeClose, sizeDone, sizeOptions, sizePanel } = this.refs;

    sizeTrigger?.removeEventListener('click', this.#onSizeTriggerClick);
    sizeClose?.removeEventListener('click', this.#onSizeCloseClick);
    sizeDone?.removeEventListener('click', this.#onSizeDoneClick);
    if (sizePanel instanceof HTMLDialogElement) {
      sizePanel.removeEventListener('cancel', this.#onSizeDialogCancel);
    }

    if (sizeOptions) {
      const options = Array.isArray(sizeOptions) ? sizeOptions : [sizeOptions];
      options.forEach((button) => {
        if (button instanceof HTMLButtonElement) {
          button.removeEventListener('click', this.#onSizeOptionClick);
        }
      });
    }

    this.#removeSizeGlobalListeners();
  }

  #setupColorPicker() {
    const { colorPicker, colorTrigger, colorClose, colorDone, colorPanel } = this.refs;
    if (!(colorPicker instanceof HTMLElement) || !(colorTrigger instanceof HTMLButtonElement)) return;

    colorTrigger.addEventListener('click', this.#onColorTriggerClick);
    colorClose?.addEventListener('click', this.#onColorCloseClick);
    colorDone?.addEventListener('click', this.#onColorDoneClick);
    if (colorPanel instanceof HTMLDialogElement) {
      colorPanel.addEventListener('cancel', this.#onColorDialogCancel);
    }

    const { colorOptions } = this.refs;
    if (colorOptions) {
      const options = Array.isArray(colorOptions) ? colorOptions : [colorOptions];
      options.forEach((button) => {
        if (button instanceof HTMLButtonElement) button.addEventListener('click', this.#onColorOptionClick);
      });
    }

    if (this.#usesFamilySwatches()) {
      this.#committedFamilyHandle = this.dataset.currentFamilyHandle ?? null;
      this.#committedColorValue = this.dataset.currentFamilyColor ?? null;
      this.#familySwatchClickHandler = this.#onFamilySwatchClick;
      if (colorPanel instanceof HTMLElement) {
        colorPanel.addEventListener('click', this.#familySwatchClickHandler);
      }
      return;
    }

    this.#committedColorValue = this.#getColorSelect()?.value ?? null;
  }

  #teardownColorPickerListeners() {
    const { colorTrigger, colorClose, colorDone, colorOptions, colorPicker } = this.refs;
    const colorPanel = this.#getColorPanelElement();

    colorTrigger?.removeEventListener('click', this.#onColorTriggerClick);
    colorClose?.removeEventListener('click', this.#onColorCloseClick);
    colorDone?.removeEventListener('click', this.#onColorDoneClick);
    if (colorPanel instanceof HTMLDialogElement) {
      colorPanel.removeEventListener('cancel', this.#onColorDialogCancel);
    }

    if (colorOptions) {
      const options = Array.isArray(colorOptions) ? colorOptions : [colorOptions];
      options.forEach((button) => {
        if (button instanceof HTMLButtonElement) button.removeEventListener('click', this.#onColorOptionClick);
      });
    }

    if (this.#familySwatchClickHandler && colorPanel instanceof HTMLElement) {
      colorPanel.removeEventListener('click', this.#familySwatchClickHandler);
      this.#familySwatchClickHandler = null;
    }

    this.#removeColorGlobalListeners();
  }

  /** @param {Event} event */
  #onSizeDialogCancel = (event) => {
    event.preventDefault();
    this.#closeSizePicker(false);
  };

  /** @param {Event} event */
  #onColorDialogCancel = (event) => {
    event.preventDefault();
    this.#closeColorPicker(false);
  };

  /** @param {MouseEvent} event */
  #onSizeTriggerClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.dataset.optionsDisabled !== undefined) return;

    const sizePanel = this.#getSizePanelElement();
    if (!(sizePanel instanceof HTMLElement)) return;

    if (!this.#isPanelOpen(sizePanel)) {
      this.#openSizePicker();
    } else {
      this.#closeSizePicker(false);
    }
  };

  /** @param {MouseEvent} event */
  #onSizeCloseClick = (event) => {
    event.preventDefault();
    this.#closeSizePicker(false);
  };

  /** @param {MouseEvent} event */
  #onSizeDoneClick = async (event) => {
    event.preventDefault();

    const sizeSelect = this.#getSizeSelect();
    const originalValue = this.#committedSizeValue ?? sizeSelect?.value;
    const pendingValue = this.#pendingSizeValue ?? sizeSelect?.value;

    if (!sizeSelect || !pendingValue) {
      this.#closeSizePicker(false);
      return;
    }

    if (pendingValue === originalValue) {
      this.#closeSizePicker(true);
      return;
    }

    sizeSelect.value = pendingValue;
    this.#committedSizeValue = pendingValue;
    this.#closeSizePicker(true);
    await this.#updateVariant();
  };

  /** @param {Event} event */
  #onSizeOptionClick = (event) => {
    if (!(event.currentTarget instanceof HTMLButtonElement)) return;

    const value = event.currentTarget.dataset.value;
    if (!value) return;

    this.#pendingSizeValue = value;
    this.#updateSizePickerModalSelection(value);
  };

  /** @param {MouseEvent} event */
  #onColorTriggerClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.dataset.optionsDisabled !== undefined) return;

    const colorPanel = this.#getColorPanelElement();
    if (!(colorPanel instanceof HTMLElement)) return;

    if (!this.#isPanelOpen(colorPanel)) {
      this.#openColorPicker();
    } else {
      this.#closeColorPicker(false);
    }
  };

  /** @param {MouseEvent} event */
  #onColorCloseClick = (event) => {
    event.preventDefault();
    this.#closeColorPicker(false);
  };

  /** @param {MouseEvent} event */
  #onColorDoneClick = async (event) => {
    event.preventDefault();

    if (this.#usesFamilySwatches()) {
      const originalHandle = this.#normalizeHandle(
        this.#committedFamilyHandle ?? this.dataset.currentFamilyHandle ?? null
      );
      const pendingHandle = this.#normalizeHandle(this.#pendingFamilyHandle ?? originalHandle);

      if (!pendingHandle) {
        this.#closeColorPicker(false);
        return;
      }

      if (pendingHandle === originalHandle) {
        this.#closeColorPicker(true);
        return;
      }

      // Check size availability before closing — unavailable colors used to fail silently.
      const match = await this.#findFamilyVariantForHandle(pendingHandle);
      if (!match.variant) {
        this.#setColorAvailabilityError(true);
        return;
      }

      this.#setColorAvailabilityError(false);
      this.#closeColorPicker(false);
      const sectionId = this.dataset.sectionId;
      const swapped = await this.#swapToVariantId(match.variant.id);

      if (swapped && sectionId) {
        await sectionRenderer.renderSection(sectionId, { cache: false });
      }
      return;
    }

    const colorSelect = this.#getColorSelect();
    const originalValue = this.#committedColorValue ?? colorSelect?.value;
    const pendingValue = this.#pendingColorValue ?? colorSelect?.value;

    if (!colorSelect || !pendingValue) {
      this.#closeColorPicker(false);
      return;
    }

    if (pendingValue === originalValue) {
      this.#closeColorPicker(true);
      return;
    }

    colorSelect.value = pendingValue;
    this.#committedColorValue = pendingValue;
    this.#closeColorPicker(true);
    await this.#updateVariant();
  };

  /** @param {Event} event */
  #onColorOptionClick = (event) => {
    if (!(event.currentTarget instanceof HTMLButtonElement)) return;

    const value = event.currentTarget.dataset.value;
    if (!value) return;

    this.#pendingColorValue = value;
    this.#updateColorPickerSelection(value);
  };

  #openSizePicker() {
    const { sizeTrigger, sizePicker } = this.refs;
    const sizePanel = this.#getSizePanelElement();
    const sizeSelect = this.#getSizeSelect();

    if (!(sizePanel instanceof HTMLElement) || !(sizeTrigger instanceof HTMLButtonElement) || !sizeSelect) {
      return;
    }

    document.querySelectorAll('cart-line-options .cart-size-picker, cart-line-options .cart-color-picker').forEach((picker) => {
      if (!(picker instanceof HTMLElement) || picker === sizePicker) return;
      this.#hidePickerPanels(picker);
    });
    const openColorPanel = this.#getColorPanelElement();
    if (openColorPanel instanceof HTMLElement && this.#isPanelOpen(openColorPanel)) {
      this.#closePanel(openColorPanel);
    }

    this.#committedSizeValue = sizeSelect.value;
    this.#pendingSizeValue = sizeSelect.value;
    this.#updateSizePickerModalSelection(sizeSelect.value);

    this.#openPanel(sizePanel, sizePicker instanceof HTMLElement ? sizePicker : undefined);
    sizeTrigger.setAttribute('aria-expanded', 'true');
    sizePicker?.classList.add('is-open');

    if (!this.#usesCenteredModals()) {
      this.#sizeOutsideClickHandler = (event) => {
        if (document.querySelector('dialog.size-chart-drawer__dialog[open]')) return;
        if (!(event.target instanceof Node)) return;
        if (sizePicker?.contains(event.target)) return;
        if (sizePanel.contains(event.target)) return;
        this.#closeSizePicker(false);
      };

      document.addEventListener('click', this.#sizeOutsideClickHandler);

      this.#sizeKeydownHandler = (event) => {
        if (event.key !== 'Escape') return;
        if (document.querySelector('dialog.size-chart-drawer__dialog[open]')) return;
        this.#closeSizePicker(false);
      };

      document.addEventListener('keydown', this.#sizeKeydownHandler);
    }
  }

  #openColorPicker() {
    const { colorTrigger, colorPicker } = this.refs;
    const colorPanel = this.#getColorPanelElement();

    if (!(colorPanel instanceof HTMLElement) || !(colorTrigger instanceof HTMLButtonElement)) {
      return;
    }

    document.querySelectorAll('cart-line-options .cart-size-picker, cart-line-options .cart-color-picker').forEach((picker) => {
      if (!(picker instanceof HTMLElement) || picker === colorPicker) return;
      this.#hidePickerPanels(picker);
    });
    const openSizePanel = this.#getSizePanelElement();
    if (openSizePanel instanceof HTMLElement && this.#isPanelOpen(openSizePanel)) {
      this.#closePanel(openSizePanel);
    }

    if (this.#usesFamilySwatches()) {
      this.#pendingFamilyHandle = this.#committedFamilyHandle ?? this.dataset.currentFamilyHandle ?? null;
      this.#pendingColorValue = this.#committedColorValue ?? this.dataset.currentFamilyColor ?? null;
      this.#setColorAvailabilityError(false);

      if (this.#pendingFamilyHandle) {
        this.#updateFamilySwatchModalSelection(this.#pendingFamilyHandle);
      }

      this.#openPanel(colorPanel, colorPicker instanceof HTMLElement ? colorPicker : undefined);
      colorTrigger.setAttribute('aria-expanded', 'true');
      colorPicker?.classList.add('is-open');
      void this.#refreshFamilySwatchAvailability();
      return;
    }

    const colorSelect = this.#getColorSelect();

    if (!colorSelect) {
      return;
    }

    this.#committedColorValue = colorSelect.value;
    this.#pendingColorValue = colorSelect.value;
    this.#updateColorPickerSelection(colorSelect.value);

    this.#openPanel(colorPanel, colorPicker instanceof HTMLElement ? colorPicker : undefined);
    colorTrigger.setAttribute('aria-expanded', 'true');
    colorPicker?.classList.add('is-open');

    if (!this.#usesCenteredModals()) {
      this.#colorOutsideClickHandler = (event) => {
        if (!(event.target instanceof Node)) return;
        if (colorPicker?.contains(event.target)) return;
        if (colorPanel.contains(event.target)) return;
        this.#closeColorPicker(false);
      };

      document.addEventListener('click', this.#colorOutsideClickHandler);

      this.#colorKeydownHandler = (event) => {
        if (event.key === 'Escape') {
          this.#closeColorPicker(false);
        }
      };

      document.addEventListener('keydown', this.#colorKeydownHandler);
    }
  }

  /**
   * @param {boolean} keepSelection
   */
  #closeSizePicker(keepSelection) {
    const { sizeTrigger } = this.refs;
    const sizePanel = this.#getSizePanelElement();
    const sizeSelect = this.#getSizeSelect();

    if (!(sizePanel instanceof HTMLElement) || !(sizeTrigger instanceof HTMLButtonElement)) return;

    const valueToShow = keepSelection
      ? (this.#pendingSizeValue ?? this.#committedSizeValue ?? sizeSelect?.value)
      : (this.#committedSizeValue ?? sizeSelect?.value);

    if (valueToShow) {
      this.#updateSizePickerModalSelection(valueToShow);
      if (keepSelection) {
        this.#setSizeTriggerText(valueToShow);
      }
      if (sizeSelect && !keepSelection) {
        sizeSelect.value = valueToShow;
      }
    }

    this.#closePanel(sizePanel);
    sizeTrigger.setAttribute('aria-expanded', 'false');
    this.refs.sizePicker?.classList.remove('is-open');
    this.#pendingSizeValue = null;
    this.#removeSizeGlobalListeners();
  }

  /**
   * @param {boolean} keepSelection
   */
  #closeColorPicker(keepSelection) {
    const { colorTrigger } = this.refs;
    const colorPanel = this.#getColorPanelElement();
    const colorSelect = this.#getColorSelect();

    if (!(colorPanel instanceof HTMLElement)) return;

    if (colorTrigger instanceof HTMLButtonElement) {
      colorTrigger.setAttribute('aria-expanded', 'false');
    }
    this.refs.colorPicker?.classList.remove('is-open');

    if (this.#usesFamilySwatches()) {
      const handleToShow = keepSelection
        ? (this.#pendingFamilyHandle ?? this.#committedFamilyHandle ?? this.dataset.currentFamilyHandle ?? null)
        : (this.#committedFamilyHandle ?? this.dataset.currentFamilyHandle ?? null);
      const nameToShow = keepSelection
        ? (this.#pendingColorValue ?? this.#committedColorValue ?? this.dataset.currentFamilyColor ?? null)
        : (this.#committedColorValue ?? this.dataset.currentFamilyColor ?? null);

      if (handleToShow) {
        this.#updateFamilySwatchModalSelection(handleToShow);
      }

      if (keepSelection && nameToShow) {
        this.#setColorTriggerText(nameToShow);
      }

      this.#closePanel(colorPanel);
      this.#pendingColorValue = null;
      this.#pendingFamilyHandle = null;
      this.#removeColorGlobalListeners();
      return;
    }

    const valueToShow = keepSelection
      ? (this.#pendingColorValue ?? this.#committedColorValue ?? colorSelect?.value)
      : (this.#committedColorValue ?? colorSelect?.value);

    if (valueToShow) {
      this.#updateColorPickerSelection(valueToShow);
      if (keepSelection) {
        this.#setColorTriggerText(valueToShow);
      }
      if (colorSelect && !keepSelection) colorSelect.value = valueToShow;
    }

    this.#closePanel(colorPanel);
    this.#pendingColorValue = null;
    this.#removeColorGlobalListeners();
  }

  #removeSizeGlobalListeners() {
    if (this.#sizeOutsideClickHandler) {
      document.removeEventListener('click', this.#sizeOutsideClickHandler);
      this.#sizeOutsideClickHandler = null;
    }

    if (this.#sizeKeydownHandler) {
      document.removeEventListener('keydown', this.#sizeKeydownHandler);
      this.#sizeKeydownHandler = null;
    }
  }

  #removeColorGlobalListeners() {
    if (this.#colorOutsideClickHandler) {
      document.removeEventListener('click', this.#colorOutsideClickHandler);
      this.#colorOutsideClickHandler = null;
    }

    if (this.#colorKeydownHandler) {
      document.removeEventListener('keydown', this.#colorKeydownHandler);
      this.#colorKeydownHandler = null;
    }
  }

  /** @returns {HTMLButtonElement[]} */
  #getSizeOptionButtons() {
    const { sizePanel, sizeOptions } = this.refs;

    if (sizePanel instanceof HTMLElement) {
      /** @type {HTMLButtonElement[]} */
      const panelButtons = [];
      sizePanel.querySelectorAll('.cart-size-picker__option').forEach((button) => {
        if (button instanceof HTMLButtonElement) panelButtons.push(button);
      });
      if (panelButtons.length) return panelButtons;
    }

    if (sizeOptions) {
      return Array.isArray(sizeOptions) ? sizeOptions : [sizeOptions];
    }

    return [];
  }

  /**
   * @param {string | undefined | null} a
   * @param {string | undefined | null} b
   */
  #optionValuesMatch(a, b) {
    return String(a ?? '').trim() === String(b ?? '').trim();
  }

  /** @param {string} value */
  #updateSizePickerModalSelection(value) {
    const { sizeHeadingValue } = this.refs;

    if (sizeHeadingValue instanceof HTMLElement) {
      sizeHeadingValue.textContent = ` / ${value}`;
    }

    this.#getSizeOptionButtons().forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      const isSelected = this.#optionValuesMatch(button.dataset.value, value);
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });
  }

  /** @param {string} value */
  #setSizeTriggerText(value) {
    const { sizeTriggerText, sizeTrigger } = this.refs;

    if (sizeTriggerText instanceof HTMLElement) {
      const label =
        sizeTrigger instanceof HTMLButtonElement
          ? (sizeTrigger.dataset.sizeLabel ?? 'Size')
          : 'Size';
      sizeTriggerText.textContent = `${label} ${value}`;
    }
  }

  /** @param {string} value */
  #updateSizePickerSelection(value) {
    this.#updateSizePickerModalSelection(value);
    this.#setSizeTriggerText(value);
  }

  /**
   * Updates the modal heading and swatch highlight only.
   * The cart-line trigger text is intentionally NOT touched here so that opening
   * the modal never changes the collapsed label (it stays the plain color value).
   *
   * @param {string} value
   */
  #updateColorPickerSelection(value) {
    const { colorHeadingValue, colorOptions } = this.refs;

    if (colorHeadingValue instanceof HTMLElement) {
      colorHeadingValue.textContent = ` / ${value}`;
    }

    if (colorOptions) {
      const options = Array.isArray(colorOptions) ? colorOptions : [colorOptions];
      options.forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) return;

        const isSelected = button.dataset.value === value;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      });
    }
  }

  /** @param {string | null | undefined} handle */
  #normalizeHandle(handle) {
    return handle?.trim().toLowerCase() ?? '';
  }

  /** @returns {HTMLElement | null} */
  #getFamilySwatchesRoot() {
    const colorPanel = this.#getColorPanelElement();
    const { colorPicker } = this.refs;

    if (colorPanel instanceof HTMLElement) {
      const panelSwatches = colorPanel.querySelector('.family-color-swatches');
      if (panelSwatches instanceof HTMLElement) return panelSwatches;
    }

    const pickerSwatches = colorPicker?.querySelector('.family-color-swatches');
    return pickerSwatches instanceof HTMLElement ? pickerSwatches : null;
  }

  /**
   * @param {{ options?: string[], option1?: string | null, option2?: string | null, option3?: string | null }} variant
   * @param {number} position
   */
  #getVariantOptionAt(variant, position) {
    if (Array.isArray(variant.options) && variant.options[position] != null) {
      return variant.options[position];
    }

    if (position === 0) return variant.option1 ?? null;
    if (position === 1) return variant.option2 ?? null;
    if (position === 2) return variant.option3 ?? null;

    return null;
  }

  /** @param {string} handle */
  #updateFamilySwatchModalSelection(handle) {
    const { colorHeadingValue } = this.refs;
    const colorPanel = this.#getColorPanelElement();
    const familySwatches = this.#getFamilySwatchesRoot();
    const normalizedHandle = this.#normalizeHandle(handle);

    if (!(familySwatches instanceof HTMLElement)) return;

    /** @type {HTMLElement | null} */
    let selectedItem = null;
    /** @type {string} */
    let selectedName = '';

    familySwatches.querySelectorAll('[data-family-swatch-item]').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      const itemHandle = this.#normalizeHandle(
        item.dataset.familySwatchHandle ||
          item.dataset.familySwatchUrl?.match(/\/products\/([^/?#]+)/)?.[1]
      );
      const isSelected = itemHandle === normalizedHandle;

      item.classList.toggle('family-color-swatches__item--active', isSelected);

      if (isSelected) {
        item.setAttribute('aria-current', 'true');
        selectedItem = item;
        selectedName = item.dataset.familySwatchName || item.getAttribute('aria-label') || '';
      } else {
        item.removeAttribute('aria-current');
      }
    });

    if (colorHeadingValue instanceof HTMLElement && selectedName) {
      colorHeadingValue.textContent = ` / ${selectedName}`;
    }

    const imageUrl = selectedItem?.dataset.familySwatchImageUrl?.trim();
    const modalImage = colorPanel?.querySelector('.cart-color-picker__image');

    if (modalImage instanceof HTMLImageElement && imageUrl) {
      modalImage.src = imageUrl;
      modalImage.removeAttribute('srcset');
      modalImage.removeAttribute('sizes');
    }

    return selectedItem;
  }

  /**
   * @param {boolean} visible
   */
  #setColorAvailabilityError(visible) {
    const colorPanel = this.#getColorPanelElement();
    const colorAvailabilityError =
      colorPanel?.querySelector('.cart-color-picker__availability-error') || this.refs.colorAvailabilityError;

    if (!(colorAvailabilityError instanceof HTMLElement)) return;

    if (visible) {
      colorAvailabilityError.hidden = false;
      colorAvailabilityError.removeAttribute('hidden');
    } else {
      colorAvailabilityError.hidden = true;
      colorAvailabilityError.setAttribute('hidden', '');
    }
  }

  /**
   * @param {string | null | undefined} left
   * @param {string | null | undefined} right
   */
  #optionsMatch(left, right) {
    return (
      String(left ?? '')
        .trim()
        .toLowerCase() ===
      String(right ?? '')
        .trim()
        .toLowerCase()
    );
  }

  /**
   * @param {string} handle
   */
  async #fetchFamilyProduct(handle) {
    const normalizedHandle = this.#normalizeHandle(handle);
    if (!normalizedHandle) return null;

    const cached = this.#familyProductCache.get(normalizedHandle);
    if (cached) return cached;

    const response = await fetch(`/products/${normalizedHandle}.js`);
    if (!response.ok) return null;

    const product = await response.json();
    this.#familyProductCache.set(normalizedHandle, product);
    return product;
  }

  /**
   * Finds an available variant on a sibling product that matches the cart line size.
   * @param {string} handle
   * @returns {Promise<{ variant: { id: number, available: boolean } | null, reason: 'ok' | 'unavailable' | 'missing' | 'error' }>}
   */
  async #findFamilyVariantForHandle(handle) {
    try {
      const product = await this.#fetchFamilyProduct(handle);
      if (!product?.variants?.length) {
        return { variant: null, reason: 'missing' };
      }

      const sizeValue = this.#getSizeSelect()?.value?.trim() ?? null;
      const sizePosition = Number(this.#getSizeSelect()?.dataset.optionPosition) - 1;
      const hasSizeConstraint = sizeValue != null && !Number.isNaN(sizePosition) && sizePosition >= 0;

      if (hasSizeConstraint) {
        const sizeVariant = product.variants.find((entry) =>
          this.#optionsMatch(this.#getVariantOptionAt(entry, sizePosition), sizeValue)
        );

        if (!sizeVariant) {
          return { variant: null, reason: 'unavailable' };
        }

        if (!sizeVariant.available) {
          return { variant: null, reason: 'unavailable' };
        }

        return { variant: sizeVariant, reason: 'ok' };
      }

      const available = product.variants.find((entry) => entry.available) ?? null;
      if (!available) {
        return { variant: null, reason: 'unavailable' };
      }

      return { variant: available, reason: 'ok' };
    } catch (error) {
      console.error(error);
      return { variant: null, reason: 'error' };
    }
  }

  /**
   * Marks family swatches unavailable for the currently selected size.
   */
  async #refreshFamilySwatchAvailability() {
    const familySwatches = this.#getFamilySwatchesRoot();
    if (!(familySwatches instanceof HTMLElement)) return;

    const items = [...familySwatches.querySelectorAll('[data-family-swatch-item]')];
    await Promise.all(
      items.map(async (item) => {
        if (!(item instanceof HTMLElement)) return;

        const handle = this.#normalizeHandle(
          item.dataset.familySwatchHandle ||
            item.dataset.familySwatchUrl?.match(/\/products\/([^/?#]+)/)?.[1]
        );
        if (!handle) return;

        const match = await this.#findFamilyVariantForHandle(handle);
        const isUnavailable = match.reason === 'unavailable' || match.reason === 'missing';

        item.classList.toggle('is-unavailable', isUnavailable);
        item.toggleAttribute('aria-disabled', isUnavailable);
        if (isUnavailable) {
          item.title = `${item.dataset.familySwatchName || item.getAttribute('aria-label') || ''} — ${
            window.Theme?.translations?.unavailable || 'Unavailable'
          }`;
        } else {
          item.title = item.dataset.familySwatchName || item.getAttribute('aria-label') || '';
        }
      })
    );
  }

  /**
   * Loads the sibling product image when Liquid could not resolve it via all_products.
   * @param {string} handle
   * @param {HTMLElement | null} swatchItem
   */
  async #ensureFamilySwatchPreviewImage(handle, swatchItem) {
    const colorPanel = this.#getColorPanelElement();
    if (!(colorPanel instanceof HTMLElement)) return;

    let modalImage = colorPanel.querySelector('.cart-color-picker__image');
    if (!(modalImage instanceof HTMLImageElement)) {
      const media = colorPanel.querySelector('.cart-color-picker__media');
      if (!(media instanceof HTMLElement)) return;
      modalImage = document.createElement('img');
      modalImage.className = 'cart-color-picker__image';
      modalImage.alt = '';
      media.replaceChildren(modalImage);
    }

    try {
      const product = await this.#fetchFamilyProduct(handle);
      const imageUrl = product?.featured_image || product?.images?.[0];
      if (!imageUrl) return;

      if (swatchItem instanceof HTMLElement) {
        swatchItem.dataset.familySwatchImageUrl = imageUrl;
      }

      if (this.#normalizeHandle(this.#pendingFamilyHandle) === handle) {
        modalImage.src = imageUrl;
        modalImage.removeAttribute('srcset');
        modalImage.removeAttribute('sizes');
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * @param {string} handle
   * @returns {Promise<boolean>}
   */
  async #swapFamilyProduct(handle) {
    const match = await this.#findFamilyVariantForHandle(handle);
    if (!match.variant) return false;

    const sectionId = this.dataset.sectionId;
    const swapped = await this.#swapToVariantId(match.variant.id);
    if (!swapped) return false;

    if (sectionId) {
      await sectionRenderer.renderSection(sectionId, { cache: false });
    }

    return true;
  }

  /** @param {Event} event */
  #onFamilySwatchClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const colorPanel = this.#getColorPanelElement();
    if (!(colorPanel instanceof HTMLElement) || !colorPanel.contains(event.target)) return;

    const swatchItem = event.target.closest('[data-family-swatch-item]');
    if (!(swatchItem instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const handle = this.#normalizeHandle(
      swatchItem.dataset.familySwatchHandle ||
        swatchItem.dataset.familySwatchUrl?.match(/\/products\/([^/?#]+)/)?.[1]
    );
    const name = swatchItem.dataset.familySwatchName || swatchItem.getAttribute('aria-label') || '';

    if (!handle) return;

    this.#pendingFamilyHandle = handle;
    this.#pendingColorValue = name;
    this.#updateFamilySwatchModalSelection(handle);
    this.#setColorAvailabilityError(swatchItem.classList.contains('is-unavailable'));
    void this.#ensureFamilySwatchPreviewImage(handle, swatchItem);
  };

  /**
   * Sets the collapsed cart-line trigger text to the plain color value only.
   * @param {string} value
   */
  #setColorTriggerText(value) {
    const { colorTriggerText } = this.refs;
    if (colorTriggerText instanceof HTMLElement) {
      colorTriggerText.textContent = value;
    }
  }

  /** @returns {HTMLSelectElement | null} */
  #getSizeSelect() {
    const { optionSelects } = this.refs;
    if (!optionSelects) return null;

    const selects = Array.isArray(optionSelects) ? optionSelects : [optionSelects];
    return selects.find((select) => select.classList.contains('cart-line-options__select--size')) ?? null;
  }

  /** @returns {HTMLSelectElement | null} */
  #getColorSelect() {
    const { optionSelects } = this.refs;
    if (!optionSelects) return null;

    const selects = Array.isArray(optionSelects) ? optionSelects : [optionSelects];
    return selects.find((select) => select.classList.contains('cart-line-options__select--color')) ?? null;
  }

  /** @returns {Variant[]} */
  #getVariants() {
    const { variantsData } = this.refs;
    if (!(variantsData instanceof HTMLScriptElement)) return [];

    try {
      return JSON.parse(variantsData.textContent || '[]');
    } catch {
      return [];
    }
  }

  /** @returns {string[]} */
  #getSelectedOptions() {
    const { optionSelects } = this.refs;
    if (!optionSelects) return [];

    const selects = Array.isArray(optionSelects) ? optionSelects : [optionSelects];
    const variants = this.#getVariants();
    const optionCount = variants[0]?.options?.length ?? selects.length;

    /** @type {string[]} */
    const options = Array.from({ length: optionCount }, () => '');

    selects.forEach((select) => {
      const position = Number(select.dataset.optionPosition);
      if (position > 0 && position <= optionCount) {
        options[position - 1] = select.value;
      }
    });

    return options;
  }

  /**
   * @param {Variant[]} variants
   * @param {string[]} options
   */
  #findVariant(variants, options) {
    return variants.find((variant) => options.every((option, index) => variant.options[index] === option));
  }

  /** @returns {string[]} */
  #getSectionIds() {
    /** @type {Set<string>} */
    const ids = new Set();

    if (this.dataset.sectionId) {
      ids.add(this.dataset.sectionId);
    }

    document.querySelectorAll('cart-items-component').forEach((element) => {
      if (element instanceof HTMLElement && element.dataset.sectionId) {
        ids.add(element.dataset.sectionId);
      }
    });

    return Array.from(ids);
  }

  /** @param {HTMLSelectElement} select */
  async #updateQuantity(select) {
    const line = Number(select.dataset.cartLine);
    const quantity = Number(select.value);

    if (!line || Number.isNaN(quantity)) return;

    await this.#requestCartChange({ line, quantity });
  }

  async #updateVariant() {
    const variants = this.#getVariants();
    const options = this.#getSelectedOptions();
    const variant = this.#findVariant(variants, options);

    if (!variant?.id) {
      this.#resetOptionSelects();
      return;
    }

    if (!variant.available) {
      this.#resetOptionSelects();
      return;
    }

    await this.#swapToVariantId(variant.id);
  }

  /**
   * @typedef {object} CartAddPayload
   * @property {number} id
   * @property {number} quantity
   * @property {Record<string, string>} [properties]
   * @property {number} [selling_plan]
   */

  /**
   * @param {Array<{ variant_id: number, quantity: number, properties?: Record<string, string>, selling_plan_allocation?: { selling_plan?: { id?: number } } }>} items
   * @returns {CartAddPayload[]}
   */
  #cartItemsToAddPayload(items) {
    return items.map((item) => {
      /** @type {CartAddPayload} */
      const payload = { id: item.variant_id, quantity: item.quantity };

      if (item.properties && Object.keys(item.properties).length > 0) {
        payload.properties = { ...item.properties };
      }

      const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id;
      if (sellingPlanId) payload.selling_plan = sellingPlanId;

      return payload;
    });
  }

  /**
   * @param {Array<{ variant_id: number }>} cartItems
   * @param {CartAddPayload[]} desiredItems
   * @returns {boolean}
   */
  #cartMatchesDesiredOrder(cartItems, desiredItems) {
    if (cartItems.length !== desiredItems.length) return false;

    return cartItems.every((item, index) => item.variant_id === desiredItems[index]?.id);
  }

  /**
   * @param {CartAddPayload[]} items
   * @param {string} sections
   * @returns {Promise<Record<string, any> | null>}
   */
  async #rebuildCartWithItems(items, sections) {
    const clearResponse = await fetch(
      `${Theme.routes.cart_url}/clear.js`,
      fetchConfig('json', { body: JSON.stringify({}) })
    );

    if (!clearResponse.ok) return null;

    const addResponse = await fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', {
        body: JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname,
        }),
      })
    );

    const addData = await addResponse.json();
    if (addData.status) return null;

    return addData;
  }

  /**
   * @param {number} variantId
   * @returns {Promise<boolean>}
   */
  async #swapToVariantId(variantId) {
    const currentVariantId = Number(this.dataset.variantId);
    const lineKey = this.dataset.lineKey;
    const quantity = Number(this.dataset.quantity);
    const sectionId = this.dataset.sectionId;

    if (!variantId || !lineKey) {
      this.#resetOptionSelects();
      return false;
    }

    if (variantId === currentVariantId) return false;

    const sections = this.#getSectionIds().join(',');

    this.#isUpdating = true;

    try {
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
      if (!cartResponse.ok) throw new Error('Failed to fetch cart');

      const cart = await cartResponse.json();
      const targetIndex = cart.items.findIndex((/** @type {{ key: string }} */ item) => item.key === lineKey);

      if (targetIndex === -1) {
        this.#resetOptionSelects();
        return false;
      }

      const note = cart.note;
      const attributes = cart.attributes;
      const rollbackItems = this.#cartItemsToAddPayload(cart.items);

      /** @type {CartAddPayload[]} */
      const desiredItems = cart.items.map((/** @type {{ key: string, variant_id: number, quantity: number, properties?: Record<string, string>, selling_plan_allocation?: { selling_plan?: { id?: number } } }} */ item, /** @type {number} */ index) => {
        const isTarget = index === targetIndex;
        /** @type {CartAddPayload} */
        const payload = {
          id: isTarget ? variantId : item.variant_id,
          quantity: isTarget ? quantity : item.quantity,
        };

        if (item.properties && Object.keys(item.properties).length > 0) {
          payload.properties = { ...item.properties };
        }

        const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id;
        if (!isTarget && sellingPlanId) payload.selling_plan = sellingPlanId;

        return payload;
      });

      // Shopify add.js order is inconsistent (LIFO vs FIFO). Try reverse display order first.
      let addData = await this.#rebuildCartWithItems([...desiredItems].reverse(), sections);

      if (!addData) {
        await this.#rebuildCartWithItems([...rollbackItems].reverse(), sections);
        this.#resetOptionSelects();
        return false;
      }

      const verifyResponse = await fetch(`${Theme.routes.cart_url}.js`);
      const verifiedCart = verifyResponse.ok ? await verifyResponse.json() : null;

      if (verifiedCart && !this.#cartMatchesDesiredOrder(verifiedCart.items, desiredItems)) {
        addData = await this.#rebuildCartWithItems(desiredItems, sections);

        if (!addData) {
          await this.#rebuildCartWithItems([...rollbackItems].reverse(), sections);
          this.#resetOptionSelects();
          return false;
        }
      }

      if (note || (attributes && Object.keys(attributes).length > 0)) {
        await fetch(
          Theme.routes.cart_update_url,
          fetchConfig('json', {
            body: JSON.stringify({ note, attributes }),
          })
        );
      }

      await this.#refreshCart(addData, sectionId);
      return true;
    } catch (error) {
      console.error(error);
      this.#resetOptionSelects();
      return false;
    } finally {
      this.#isUpdating = false;
    }
  }

  /**
   * @param {{ line: number, quantity: number }} config
   */
  async #requestCartChange(config) {
    const sections = this.#getSectionIds().join(',');
    const sectionId = this.dataset.sectionId;

    this.#isUpdating = true;

    try {
      const response = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig('json', {
          body: JSON.stringify({
            ...config,
            sections,
            sections_url: window.location.pathname,
          }),
        })
      );

      const data = await response.json();
      if (data.errors) return;

      await this.#refreshCart(data, sectionId);
    } catch (error) {
      console.error(error);
    } finally {
      this.#isUpdating = false;
    }
  }

  /**
   * @param {Record<string, any>} data
   * @param {string | undefined} [sectionIdOverride]
   */
  async #refreshCart(data, sectionIdOverride) {
    const sectionId = sectionIdOverride || this.dataset.sectionId;
    if (!sectionId) return;

    const cartItemsComponent =
      this.closest('cart-items-component') || document.querySelector('cart-items-component');
    const cartTotal = cartItemsComponent?.querySelector('text-component[ref="cartTotal"]');

    if (cartTotal && 'shimmer' in cartTotal && typeof cartTotal.shimmer === 'function') {
      cartTotal.shimmer();
    }

    try {
      if (data.sections?.[sectionId]) {
        await morphSection(sectionId, data.sections[sectionId]);
      } else {
        await sectionRenderer.renderSection(sectionId, { cache: false });
      }
    } catch (error) {
      console.error('[cart-line-options] Section morph failed, re-rendering', error);
      await sectionRenderer.renderSection(sectionId, { cache: false });
    }

    cartItemsComponent?.dispatchEvent(
      new CartUpdateEvent(data, sectionId, {
        itemCount: data.item_count,
        source: 'cart-line-options',
        sections: data.sections,
      })
    );
  }

  #resetOptionSelects() {
    const { optionSelects } = this.refs;
    if (!optionSelects) return;

    const selects = Array.isArray(optionSelects) ? optionSelects : [optionSelects];
    selects.forEach((select) => {
      const selected = select.querySelector('option[selected]');
      if (selected instanceof HTMLOptionElement) {
        select.value = selected.value;
      }

      if (select.classList.contains('cart-line-options__select--size')) {
        this.#committedSizeValue = select.value;
        this.#updateSizePickerSelection(select.value);
      }

      if (select.classList.contains('cart-line-options__select--color')) {
        this.#committedColorValue = select.value;
        this.#updateColorPickerSelection(select.value);
        this.#setColorTriggerText(select.value);
      }
    });
  }
}

if (!customElements.get('cart-line-options')) {
  customElements.define('cart-line-options', CartLineOptions);
}
